<template>
  <BasePage :pageTitle="__DICT__.menuOptions.WeeklyInfo" show-menu show-footer>
    <div class="container-xl">
      <Card margin="" body-class="p-0">
        <template #header>
          <ul class="nav nav-tabs card-header-tabs me-2">
            <li class="nav-item">
              <a
                  class="nav-link"
                  :class="{ 'active': activeTab === 'week' }"
                  href="#"
                  @click.prevent="activeTab = 'week'"
              >
                {{ __DICT__.weeklyDocs.weekTitle }}
              </a>
            </li>
            <li class="nav-item">
              <a
                  class="nav-link"
                  :class="{ 'active': activeTab === 'season' }"
                  href="#"
                  @click.prevent="activeTab = 'season'"
              >
                {{ __DICT__.weeklyDocs.seasonTitle }}
              </a>
            </li>
          </ul>
        </template>

        <div v-if="activeTab === 'week'" class="list-group list-group-flush">
          <template v-if="weeklyDocs.length">
            <a
                v-for="doc in weeklyDocs"
                :key="doc.fileId"
                class="list-group-item list-group-item-action d-flex align-items-center"
                :href="doc.blobUrl"
                target="_blank"
                rel="noopener noreferrer"
            >
              <IconFile class="icon me-2" :size="24" />
              {{ doc.title }}
              <div v-if="doc.loading" class="spinner-border spinner-border-sm ms-2" role="status">
                <span class="visually-hidden">{{ __DICT__.loading }}</span>
              </div>
            </a>
          </template>
          <div v-else class="list-group-item text-muted">
            {{ __DICT__.weeklyDocs.noWeekDocs }}
          </div>
        </div>

        <div v-else class="list-group list-group-flush">
          <template v-if="seasonDocs.length">
            <a
                v-for="doc in seasonDocs"
                :key="doc.fileId"
                class="list-group-item list-group-item-action d-flex align-items-center"
                :href="doc.blobUrl"
                target="_blank"
                rel="noopener noreferrer"
            >
              <IconFile class="icon me-2" :size="24" />
              {{ doc.title }}
              <div v-if="doc.loading" class="spinner-border spinner-border-sm ms-2" role="status">
                <span class="visually-hidden">{{ __DICT__.loading }}</span>
              </div>
            </a>
          </template>
          <div v-else class="list-group-item text-muted">
            {{ __DICT__.weeklyDocs.noSeasonDocs }}
          </div>
        </div>
      </Card>
    </div>
  </BasePage>
</template>

<script setup>
import BasePage from "@/components/BasePage.vue";
import { onMounted, ref, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { useWeekStore } from "@/stores/week";
import { api } from "@/services/api.js";
import { Card } from "@/components/atomic";
import { IconFile } from '@tabler/icons-vue';

const route = useRoute();
const weekStore = useWeekStore();
const activeTab = ref(route.params.tab === 'season' ? 'season' : 'week');
const weeklyDocs = ref([]);
const seasonDocs = ref([]);
const blobUrls = new Map(); // Store blob URLs for cleanup

const fetchAndCacheFile = async (doc) => {
  try {
    doc.loading = true;

    // Try to get from cache first
    const cache = await caches.open('file-cache');
    let response = await cache.match(doc.url);

    if (!response) {
      // If not in cache, fetch and cache it
      response = await fetch(doc.url, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load file');

      // Clone the response before caching
      const responseToCache = response.clone();
      await cache.put(doc.url, responseToCache);
    }

    // Create blob URL
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Store the blob URL for cleanup
    blobUrls.set(doc.fileId, blobUrl);

    // Add blobUrl to the doc object
    doc.blobUrl = blobUrl;
  } catch (error) {
    console.error(`Failed to fetch and cache file ${doc.fileId}:`, error);
    doc.error = true;
  } finally {
    doc.loading = false;
  }
};

onMounted(async () => {
  try {
    // Fetch document list
    const docs = await api.get(`/files/week/${weekStore.weekId}`);

    // Add loading and error states to each doc, split by weekId
    weeklyDocs.value = docs.filter(doc => doc.weekId !== null).map(doc => ({
      ...doc, loading: false, error: false, blobUrl: null
    }));
    seasonDocs.value = docs.filter(doc => doc.weekId === null).map(doc => ({
      ...doc, loading: false, error: false, blobUrl: null
    }));

    // Pre-fetch all documents (iterate over reactive arrays so mutations trigger updates)
    await Promise.all([...weeklyDocs.value, ...seasonDocs.value].map(doc => fetchAndCacheFile(doc)));
  } catch (error) {
    console.error('Failed to fetch weekly documents:', error);
    weeklyDocs.value = [];
    seasonDocs.value = [];
  }
});

// Cleanup blob URLs when component is unmounted
onBeforeUnmount(() => {
  for (const blobUrl of blobUrls.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  blobUrls.clear();
});
</script>

<style scoped>
.list-group-item {
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.list-group-item:hover {
  color: inherit;
}
</style>
